import { SlCheckbox } from "@shoelace-style/shoelace";
import { Schema } from "prosemirror-model";
import { Plugin } from "prosemirror-state";
import { Decoration, DecorationSet } from "prosemirror-view";
import type { Token } from "markdown-it";

import { BaseExtension } from "@/wc/text-editor/lib/core/base-extension";
import type { ParseSpecMap } from "@/wc/text-editor/lib/core/types";

/**
 * Show a checkbox in front of list items with attribute isToDoList
 * reflecting the attribute toDoState.
 *
 * The least intrusive way is probably to add widget decorations.
 * That way we don't have to create a NodeView and redefine the
 * rendering of list items.
 */

const toDoCheckboxPlugin = (): Plugin => {
    return new Plugin({
        props: {
            decorations(state) {
                const decorations: Decoration[] = [];
                state.doc.descendants((node, pos) => {
                    if (node.attrs.isToDoItem) {
                        if (node.firstChild?.isTextblock) {
                            decorations.push(
                                Decoration.widget(
                                    // Place the checkbox inside the paragraph
                                    pos + 2,
                                    (view) => {
                                        const checkbox =
                                            document.createElement(
                                                "sl-checkbox"
                                            );
                                        checkbox.checked =
                                            node.attrs?.toDoState;
                                        checkbox.style.whiteSpace = "normal";
                                        checkbox.style.display = "inline-block";
                                        // Make checkbox unselectable, to prevent glitchiness when user
                                        // makes a selection including to do items
                                        checkbox.style.userSelect = "none";
                                        checkbox.addEventListener(
                                            "sl-change",
                                            (event) =>
                                                view.dispatch(
                                                    state.tr.setNodeAttribute(
                                                        pos,
                                                        "toDoState",
                                                        (
                                                            event.target as SlCheckbox
                                                        ).checked
                                                    )
                                                )
                                        );
                                        checkbox.addEventListener(
                                            "sl-focus",
                                            // Return focus to the editor immediately
                                            () => view.focus()
                                        );
                                        return checkbox;
                                    },
                                    {
                                        ignoreSelection: true,
                                    }
                                )
                            );
                        }
                    }
                });
                return DecorationSet.create(state.doc, decorations);
            },
        },
    });
};

function listItemToDoAttrs(tokens: readonly Token[], i: number) {
    if (
        tokens?.[i + 1]?.type === "paragraph_open" &&
        tokens?.[i + 2]?.type === "inline"
    ) {
        // If the list item starts with text (and not some other kind of content) it could be
        // a task item.
        const content = tokens?.[i + 2].content;
        if (content.startsWith("[ ] ")) {
            return { isToDoItem: true, toDoState: false };
        }
        if (content.startsWith("[x] ")) {
            return { isToDoItem: true, toDoState: true };
        }
    }
    return { isToDoItem: false };
}

export class ToDoListItemExtension extends BaseExtension {
    plugins = () => [toDoCheckboxPlugin()];
    modifySchema = (currentSchema: Schema) => {
        return new Schema({
            nodes: currentSchema.spec.nodes.update("list_item", {
                ...currentSchema.nodes.list_item.spec,
                attrs: {
                    isToDoItem: { default: false },
                    toDoState: { default: false },
                },
                parseDOM: [
                    {
                        tag: "li",
                        getAttrs: (dom) => ({
                            isToDoItem: (dom as HTMLElement).hasAttribute(
                                "data-todo-list"
                            ),
                            toDoState:
                                (dom as HTMLElement).getAttribute(
                                    "data-todo-state"
                                ) === "true",
                        }),
                    },
                ],
                toDOM(node) {
                    return [
                        "li",
                        {
                            "data-todo-list": node.attrs.isToDoItem
                                ? "true"
                                : null,
                            "data-todo-state": node.attrs.isToDoItem
                                ? node.attrs.toDoState
                                    ? "true"
                                    : "false"
                                : null,
                            "style": node.attrs.isToDoItem
                                ? node.attrs.toDoState
                                    ? "list-style: none;"
                                    : "list-style: none;"
                                : "",
                        },
                        0,
                    ];
                },
            }),
            marks: currentSchema.spec.marks,
        });
    };
    modifyParser = (currentParser: ParseSpecMap): ParseSpecMap => {
        return {
            ...currentParser,
            list_item: {
                block: "list_item",
                getAttrs: (_, tokens, i) => listItemToDoAttrs(tokens, i),
            },
        };
    };
}
